import fetcher from 'utils/api';

export const toQueryString = (params: Record<string, any>): string => {
  /**
   * If `params` has a key with value undefined, then we don't add the key to the query string.
   * All other falsy values are added.
   */
  const urlParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      urlParams.append(key, value);
    }
  }

  return urlParams.toString();
};

export const buildUrl = (url: string, params: Record<string, any>) => {
  const query = toQueryString(params);

  return query ? `${url}?${query}` : url;
};

export const camelCaseToSnakeCase = (obj: Record<string, any>) => {
  const snakeCasedObj = {};

  for (const key in obj) {
    // If key already contains underscore, assume it's already snake_case
    const newKey = key.includes('_') ? key.toLowerCase() : key.replace(/([A-Z])/g, '_$1').toLowerCase();

    snakeCasedObj[newKey] = obj[key];
  }
  return snakeCasedObj;
};

export class ApiClient {
  static async get(url: string, params: Record<string, string | undefined>, ...props) {
    return fetcher(buildUrl(url, camelCaseToSnakeCase(params)), 'GET', ...props);
  }

  static async mutate<TBody, TResponse>(url: string, method: string, body: TBody, isAuthenticated = false, ...props): Promise<TResponse> {
    const snakeCasedBody = camelCaseToSnakeCase(body);

    return fetcher(url, method, isAuthenticated, snakeCasedBody, ...props) as Promise<TResponse>;
  }

  static async patch<TBody, TResponse>(url: string, body: TBody, isAuthenticated = false, ...props): Promise<TResponse> {
    return ApiClient.mutate<TBody, TResponse>(url, 'PATCH', body, isAuthenticated, ...props);
  }

  static async put<TBody, TResponse>(url: string, body: TBody, isAuthenticated = false, ...props): Promise<TResponse> {
    return ApiClient.mutate<TBody, TResponse>(url, 'PUT', body, isAuthenticated, ...props);
  }

  static async post(url: string, body: Record<string, string | undefined>, isAuthenticated = false, ...props) {
    return ApiClient.mutate(url, 'POST', body, isAuthenticated, ...props);
  }

  static async delete(url: string, body: Record<string, string | undefined>, isAuthenticated = false, ...props) {
    return ApiClient.mutate(url, 'DELETE', body, isAuthenticated, ...props);
  }
}
